import { Observable, BehaviorSubject, Subscription, combineLatest, fromEvent, of } from "rxjs";
import {distinctUntilChanged, debounceTime, bufferCount, filter, map, switchMap, tap} from "rxjs/operators";

export type SceneActivationParams = {
    resize$: Observable<Window>;
    screenHeight$: BehaviorSubject<number>;
    fallback$: BehaviorSubject<boolean>;
    progress$: Observable<number>;
    id: string;
}

export type SceneParams = {
    screenLengths: number,
}

export type SceneMountParams = {
    vizPlate: HTMLDivElement;
    // textPlate: HTMLDivElement;
}

export interface Scene {
    screenLengths: number;
    graphicContainer: HTMLDivElement;
    // textContainer: HTMLDivElement;
    activate(params: SceneActivationParams): Subscription[]
    deactivate(subs: Subscription[]): void;
    mount(params: SceneMountParams): void;
}

export type GraphicParams = {
    scenes: {[id: string]: Scene};
    sceneOrder: string[];
    mountPoint: string;
    text: any[];
    debug?: boolean;
    screenBuffer?: number;
}

export class Graphic {
    private scenes: { [id: string]: Scene } = {};
    private sceneOrder: string[] = [];
    private resize$: BehaviorSubject<Window>;
    private screenHeight$: BehaviorSubject<number>;
    private fallback$: BehaviorSubject<boolean>;
    private scrollPos$: Observable<number>;
    private scrollRel$: Observable<DOMRect>;
    private totalScreenLengths: number;
    private visualPlate: HTMLDivElement;
    private textPlate: HTMLDivElement;
    private screenBuffer: number;
    private text: any[];
    private isMounted: boolean = false;
    private activeSteps = [];
    private b1: HTMLDivElement;
    private b2: HTMLDivElement;

    private cancelOnUnmount: Subscription[] = [];

    constructor({scenes, sceneOrder, debug, mountPoint, screenBuffer, text}: GraphicParams) {
        this.scenes = scenes;
        this.text = text;
        this.sceneOrder = sceneOrder;
        this.resize$ = new BehaviorSubject(window)
        this.screenHeight$ = new BehaviorSubject(window.innerHeight - 50);
        this.fallback$ = new BehaviorSubject(false);
        this.screenBuffer = screenBuffer || .5
        // Add an extra screen lengths to get a half screen buffer before and after the graphic
        this.totalScreenLengths = Object.entries(scenes).map(([k, v]) => v.screenLengths + this.screenBuffer).reduce((p, n) => p + n, 0);
        this.scrollPos$ = fromEvent(document, "scroll").pipe(switchMap(x => of(window.scrollY)));

        this.textPlate = document.createElement("div");
        this.visualPlate = document.createElement("div");
        this.b1 = document.createElement("div");
        this.b2 = document.createElement("div");

        // Set up scroll notification
        const anchor = document.getElementById(mountPoint);
        if (!anchor) throw new Error(`Cannot find mount point ${mountPoint}`);
        this.scrollRel$ = this.scrollPos$.pipe(map<number, DOMRect>((_) => anchor.getBoundingClientRect()));




        if (debug) {
            // Set up konami code to trigger fallback change
            this.cancelOnUnmount.push(
                fromEvent<KeyboardEvent>(document, 'keydown').pipe(
                    map<KeyboardEvent, number>(e => e.keyCode),
                    bufferCount(10, 1),
                    filter(Graphic.isKonamiCode),
                ).subscribe(v => this.setFallback(!this.fallback$.getValue()))
            );

            // this.cancelOnUnmount.push(
            //   this.scrollPos$.subscribe(console.log)
            // );

            // this.cancelOnUnmount.push(
            //   this.scrollRel$.subscribe(console.log)
            // )

            // Debug logging
            // this.cancelOnUnmount.push(this.resize$.subscribe(console.log));
            // this.cancelOnUnmount.push(this.screenHeight$.subscribe(console.log));
            // this.cancelOnUnmount.push(this.fallback$.subscribe(console.log));
        }


    }

    /**
     * Explicitly sets graphic into fallback mode. Individual scenes may sub to fallback$ and handle it however they
     * see fit.
     */
    public setFallback(v: boolean): void {
        this.fallback$.next(v)
    }

    /**
     * Mount graphic to element with id=<id>
     */
    public mount(id: string): void {
        // console.log(this);
        let initialHeight = window.innerHeight;
        if (!this.isMounted) {
            const m = document.getElementById(id);
            m.appendChild(this.visualPlate);
            // m.appendChild(this.b1);
            m.appendChild(this.textPlate);
            // m.appendChild(this.b2);
            this.textPlate.style.zIndex = "10";
            this.visualPlate.style.zIndex = "1";
            this.textPlate.style.position = "relative";
            this.visualPlate.style.position = "fixed";
            this.textPlate.style.top = "0";
            this.visualPlate.style.top = "0";
            this.textPlate.style.left = "0";
            this.visualPlate.style.left = "0";
            this.textPlate.style.width = "100%";
            this.visualPlate.style.width = "100%";
            this.visualPlate.classList.add("viz-plate");
            this.textPlate.classList.add("text-plate");
        }

        // Update div heights on screen resize
        this.cancelOnUnmount.push(
            this.resize$.subscribe(x => {
                if (x.innerHeight > initialHeight && x.innerHeight - initialHeight <= 60) {
                    this.visualPlate.style.marginTop = `${x.innerHeight - initialHeight}px`;
                } else if (x.innerHeight == initialHeight) {
                    this.visualPlate.style.marginTop = "0px";
                }
                const res =  this.screenHeight$.next(Math.abs(x.innerHeight - initialHeight) <= 60 ? initialHeight : x.innerHeight);
                // initialHeight = x.innerHeight;
                return res
            })
        );
        this.cancelOnUnmount.push(
            fromEvent<Event>(window, "resize").pipe(
                debounceTime(100),
                map<Event, Window>(e => <Window>e.target)
            ).subscribe(this.resize$)
        )
        this.cancelOnUnmount.push(this.screenHeight$.subscribe(x => {
            this.textPlate.style.height = `${x * this.totalScreenLengths}px`;
            this.visualPlate.style.height = `${x}px`;
            this.b1.style.height = `${x * this.screenBuffer}`
            this.b2.style.height = `${x * this.screenBuffer}`
        }));

        // Attach text
        for (let step of this.text) {
            if (!this.isMounted) {
                if (!step.type) {
                    const p = document.createElement(step.elType || "p");
                    p.classList.add("stepper-text")
                    p.innerHTML = step.text;
                    const d = document.createElement("div")
                    d.classList.add("step")
                    d.append(p)
                    this.textPlate.append(d);
                    this.activeSteps.push({
                        el: d,
                        screenLengthPos: step.screenLengthPos
                    })
                } else {
                    switch (step.type) {
                        case "start": {
                            const h1 = document.createElement("h1")
                            h1.innerText = "POISONED";
                            const subhed = document.createElement("p")
                            subhed.classList.add("subber")
                            subhed.innerText = "A special report from the Tampa Bay Times";
                            const scrollLabel = document.createElement("div")
                            scrollLabel.id = "scroll-label";
                            scrollLabel.classList.add("started")
                            scrollLabel.innerHTML = "scroll to begin <span class='arrow'></span>"

                            const cont = document.createElement("div")
                            cont.classList.add("step")
                            cont.classList.add("headline")
                            cont.append(h1)
                            cont.append(subhed)
                            cont.append(scrollLabel)
                            this.textPlate.append(cont)
                            this.activeSteps.push({
                                el: cont,
                                screenLengthPos: 0.25
                            })
                            break;
                        }
                        case "end": {
                            const h1 = document.createElement("h2")
                            h1.innerText = "#Poisoned";
                            const subhed = document.createElement("p")
                            subhed.classList.add("subber")
                            subhed.innerHTML = "<a style='text-shadow: none;' href='https://projects.tampabay.com/projects/2021/investigations/lead-factory/gopher-workers/'>Read the story</a>";
                            const cont = document.createElement("div")
                            cont.classList.add("step")
                            // cont.classList.add("headline")
                            cont.append(h1)
                            cont.append(subhed)
                            this.textPlate.append(cont)
                            this.activeSteps.push({
                                el: cont,
                                screenLengthPos: 33.5
                            })
                            break;
                        }
                    }
                }
                //     const d = document.createElement("div")
                //     d.classList.add("headline")
                //     d.classList.add("step")
                //     const txtCont = document.createElement("div")
                //     txtCont.classList.add("textshadow")
                //     txtCont.classList.add("bottom")
                //     txtCont.classList.add("center")
                //     txtCont.classList.add("headline-overlay")
                //     const h = document.createElement("h1")
                //     h.id = "title"
                //     h.innerText = step.text;
                //     const bylineCont = document.createElement("div");
                //     bylineCont.id = "byline-box"
                //     const byp = document.createElement("p")
                //     byp.classList.add("byline-block")
                //     byp.innerText = "Corey Johnson, Rebecca Woolington, Eli Murray"
                //     bylineCont.append(byp)
                //     txtCont.append(h)
                //     txtCont.append(bylineCont)
                //     d.append(txtCont)
                //     this.textPlate.append(d);
                //     this.activeSteps.push({
                //         el: d,
                //         screenLengthPos: step.screenLengthPos
                //     })
                // }
            }
        }

        this.cancelOnUnmount.push(this.screenHeight$.subscribe(s => this.adjustStepHeight(s)))

        // Attach scene containers
        this.sceneOrder.map((id, index) => {
            const scene = this.scenes[id];
            const {graphicContainer, textContainer} = this.getContainersForScene(id);

            if (!this.isMounted) {
                graphicContainer.classList.add(`${id}-viz`);
                graphicContainer.classList.add("viz");
                graphicContainer.style.zIndex = `${100 - index}`;
                this.visualPlate.append(graphicContainer);
            }

            scene.mount({vizPlate: graphicContainer})
            // if (!this.)
        })

        // After everything has mounted,  call activate on all scenes
        this.sceneOrder.map((id, index) => {
            const scene = this.scenes[id];
            let offsetScreens = 0;
            if (index > 0) {
                const prevScenes = this.sceneOrder.slice(0, index);
                for (let s of prevScenes) {
                    offsetScreens += this.scenes[s].screenLengths
                }
            }
            // offsetScreens += this.screenBuffer

            const progress$ = combineLatest([this.scrollRel$, this.screenHeight$]).pipe(
                map(([pos, height]) => {
                    const viewMid = ((pos.top) - height / 2) + (offsetScreens * height);
                    const totalHeight = scene.screenLengths * height;
                    // console.log(totalHeight)
                    if (viewMid >= 0) return 0;
                    return (-viewMid / totalHeight) > 1 ? 1 : -viewMid / totalHeight
                }),
                distinctUntilChanged()
            )

            scene.activate({
                resize$: this.resize$,
                screenHeight$: this.screenHeight$,
                fallback$: this.fallback$,
                progress$,
                id
            })

            this.cancelOnUnmount.push(
                progress$.subscribe(p => {
                    if (index === 0) {
                        if (p === 1) {
                            scene.graphicContainer.classList.add("hidden-scene")
                        } else {
                            scene.graphicContainer.classList.remove("hidden-scene")
                        }
                    } else {
                        if (p === 0) {
                            scene.graphicContainer.classList.add("hidden-scene");
                        } else {
                            scene.graphicContainer.classList.remove("hidden-scene");
                        }
                    }
                })
            )
        })


        //
        //     const progress$ = combineLatest([this.scrollRel$, this.screenHeight$]).pipe(map(([pos, height]) => {
        //         const viewMid = (pos.top - height / 2) + (offsetScreens * height);
        //         const totalHeight = scene.screenLengths * height;
        //         if (viewMid >= 0) return 0;
        //         return (-viewMid / totalHeight) > 1 ? 1 : -viewMid / totalHeight;
        //     }), distinctUntilChanged());
        //
        //     scene.activate({
        //         resize$: this.resize$,
        //         screenHeight$: this.screenHeight$,
        //         fallback$: this.fallback$,
        //         progress$,
        //         id
        //     });
        //
        //     this.cancelOnUnmount.push(
        //         progress$.subscribe(p => {
        //             if (index === 0) {
        //                 if (p === 1) {
        //                     scene.graphicContainer.classList.add("hidden-scene")
        //                 } else {
        //                     scene.graphicContainer.classList.remove("hidden-scene")
        //                 }
        //             } else {
        //                 if (p === 0 || p === 1) {
        //                     scene.graphicContainer.classList.add("hidden-scene")
        //                     // scene.textContainer.classList.add("hidden-scene")
        //                 } else {
        //                     scene.graphicContainer.classList.remove("hidden-scene")
        //                     // scene.textContainer.classList.remove("hidden-scene")
        //                 }
        //             }
        //         })
        //     )
        // });
        this.isMounted = true;
    }

    private adjustStepHeight(x: number) {
        for (let step of this.activeSteps) {
            step.el.style.top = `${x * step.screenLengthPos}px`
        }
    }

    /**
     * Clean up dangling subscriptions
     */
    public unmount(): void {
        for (let sub of this.cancelOnUnmount) {
            sub.unsubscribe();
        }
    }

    private findSceneOffset(index: number, sh: number) {
        const prev = this.sceneOrder.slice(0, index);
        let lengths = 0
        for (let p of prev) {
            lengths += this.scenes[p].screenLengths + this.screenBuffer
        }
        return sh * lengths + this.screenBuffer;
    }

    private getContainersForScene(id: string): { graphicContainer: HTMLDivElement, textContainer: HTMLDivElement } {
        const s = this.scenes[id];
        if (!s) {
            throw new Error(`No scene registered with id ${id}`);
        }
        return {
            graphicContainer: s.graphicContainer,
            textContainer: s.textContainer
        }
    }

    private static isKonamiCode(buffer) {
        return [38, 38, 40, 40, 37, 39, 37, 39, 66, 65].toString() === buffer.toString();
    }
}
